import React from 'react'

const Section: React.FC<{ id?: string; title: string; children: React.ReactNode }> = ({ id, title, children }) => (
  <section id={id} className="mb-10 scroll-mt-24">
    <h2 className="text-2xl font-bold text-gray-900 mb-4">{title}</h2>
    <div className="space-y-3 text-gray-700 leading-relaxed">{children}</div>
  </section>
)

export const PrivacyPolicyPage: React.FC = () => {
  return (
    <div className="min-h-screen pt-16">
      <section className="py-16 bg-gradient-to-br from-kylix-primary via-purple-800 to-pink-800 text-white">
        <div className="container">
          <div className="max-w-4xl mx-auto">
            <h1 className="text-4xl font-bold mb-4">Política de Privacidade</h1>
            <p className="text-gray-200">Última atualização: 24 de setembro de 2026</p>
          </div>
        </div>
      </section>

      <section className="py-16 bg-white">
        <div className="container">
          <div className="max-w-4xl mx-auto">
            <Section title="1. Quem somos">
              <p>
                SmartData é a marca da <strong>FOCO DESENVOLVIMENTO TECNOLOGICO LTDA</strong>, inscrita no
                CNPJ sob o nº <strong>10.344.306/0001-19</strong>, com sede em Sorocaba/SP, Brasil
                ("SmartData", "nós").
              </p>
              <p>
                Esta política explica como tratamos dados pessoais no site <strong>smartdataerp.com.br</strong> e
                na plataforma SmartData (sistema de gestão e emissão de documentos fiscais usado por empresas
                clientes), de acordo com a Lei Geral de Proteção de Dados (Lei nº 13.709/2018 — LGPD).
              </p>
            </Section>

            <Section title="2. Dados que tratamos">
              <ul className="list-disc pl-6 space-y-2">
                <li>
                  <strong>Contato pelo site:</strong> nome, e-mail, telefone e a mensagem que você nos envia.
                </li>
                <li>
                  <strong>Empresas clientes da plataforma:</strong> dados cadastrais da empresa e do seu
                  responsável (nome, e-mail, telefone, CNPJ, endereço) e dados de acesso.
                </li>
                <li>
                  <strong>Clientes e fornecedores das empresas clientes:</strong> nome, CPF/CNPJ, endereço,
                  telefone e e-mail cadastrados pela empresa cliente para emitir documentos fiscais, orçamentos
                  e cobranças.
                </li>
                <li>
                  <strong>Dados técnicos:</strong> endereço IP, data e hora de acesso e registros de uso,
                  necessários para segurança e funcionamento dos serviços.
                </li>
              </ul>
              <p>
                Em relação aos dados que a empresa cliente cadastra sobre os seus próprios clientes e
                fornecedores, a empresa cliente é a controladora e a SmartData atua como operadora, tratando
                esses dados apenas conforme as instruções dela.
              </p>
            </Section>

            <Section title="3. Mensagens pelo WhatsApp">
              <p>
                A plataforma envia mensagens pelo WhatsApp Business Platform, serviço da Meta Platforms, Inc.,
                em nome das empresas clientes. São mensagens transacionais, como:
              </p>
              <ul className="list-disc pl-6 space-y-2">
                <li>disponibilização de documento fiscal (NF-e, NFC-e, NFS-e);</li>
                <li>envio de orçamento;</li>
                <li>lembrete de cobrança de parcela em aberto ou em atraso.</li>
              </ul>
              <p>
                Para isso utilizamos o número de telefone, o nome do destinatário, o conteúdo da mensagem e
                links de acesso ao documento. Esses dados são processados pela Meta na condição de provedora do
                serviço, conforme a política de privacidade do WhatsApp. Não enviamos mensagens de marketing
                pelo WhatsApp.
              </p>
              <p>
                Se não quiser mais receber essas mensagens, solicite à empresa que as enviou ou escreva para{' '}
                <a href="mailto:contato@smartdataerp.com.br" className="text-kylix-secondary underline">
                  contato@smartdataerp.com.br
                </a>.
              </p>
            </Section>

            <Section title="4. Finalidades e bases legais">
              <ul className="list-disc pl-6 space-y-2">
                <li>Prestar os serviços contratados pelas empresas clientes — execução de contrato (art. 7º, V).</li>
                <li>Emitir e guardar documentos fiscais — cumprimento de obrigação legal (art. 7º, II).</li>
                <li>Responder contatos feitos pelo site — procedimentos preliminares a contrato e consentimento (art. 7º, I e V).</li>
                <li>Manter a segurança e prevenir fraudes — legítimo interesse (art. 7º, IX).</li>
              </ul>
            </Section>

            <Section title="5. Compartilhamento">
              <p>Compartilhamos dados apenas quando necessário para prestar os serviços:</p>
              <ul className="list-disc pl-6 space-y-2">
                <li>provedores de hospedagem e infraestrutura;</li>
                <li>Meta (WhatsApp Business Platform), para o envio das mensagens descritas acima;</li>
                <li>provedores de envio de e-mail;</li>
                <li>Secretarias da Fazenda e prefeituras, para autorização de documentos fiscais;</li>
                <li>autoridades públicas, quando exigido por lei.</li>
              </ul>
              <p>Não vendemos dados pessoais.</p>
            </Section>

            <Section title="6. Retenção">
              <p>
                Mantemos os dados enquanto durar o contrato com a empresa cliente. Documentos fiscais e seus
                dados são guardados pelo prazo exigido pela legislação tributária. Links de acesso a documentos
                enviados por mensagem expiram automaticamente. Dados de contato pelo site são mantidos pelo
                tempo necessário para o atendimento.
              </p>
            </Section>

            <Section title="7. Segurança">
              <p>
                Adotamos medidas técnicas e administrativas para proteger os dados, como comunicação
                criptografada (HTTPS), controle de acesso por empresa e registro de operações.
              </p>
            </Section>

            <Section title="8. Seus direitos">
              <p>
                Você pode solicitar confirmação de tratamento, acesso, correção, anonimização, portabilidade,
                exclusão, informações sobre compartilhamento e revogação de consentimento (art. 18 da LGPD).
                Quando os dados tiverem sido cadastrados por uma empresa cliente, encaminharemos o pedido a ela,
                que é a controladora.
              </p>
            </Section>

            <Section id="exclusao-de-dados" title="9. Como solicitar a exclusão dos seus dados">
              <p>
                Envie um e-mail para{' '}
                <a href="mailto:contato@smartdataerp.com.br" className="text-kylix-secondary underline">
                  contato@smartdataerp.com.br
                </a>{' '}
                com o assunto "Exclusão de dados", informando seu nome e o telefone ou e-mail usado. Responderemos
                em até 15 dias. Dados que precisamos guardar por obrigação legal, como documentos fiscais, serão
                mantidos pelo prazo legal e depois excluídos.
              </p>
            </Section>

            <Section title="10. Cookies e armazenamento local">
              <p>
                O site não usa cookies de publicidade nem de rastreamento. Guardamos no seu navegador apenas
                preferências, como o idioma escolhido.
              </p>
            </Section>

            <Section title="11. Contato e encarregado de dados">
              <p>
                Dúvidas sobre esta política ou sobre seus dados:{' '}
                <a href="mailto:contato@smartdataerp.com.br" className="text-kylix-secondary underline">
                  contato@smartdataerp.com.br
                </a>
                . FOCO DESENVOLVIMENTO TECNOLOGICO LTDA — CNPJ 10.344.306/0001-19 — Sorocaba/SP.
              </p>
            </Section>

            <Section title="12. Alterações">
              <p>
                Podemos atualizar esta política. A data da última atualização aparece no topo desta página.
              </p>
            </Section>
          </div>
        </div>
      </section>
    </div>
  )
}
